import { api } from '@api';
import { SpriteIcon } from '@components/Sprites/SpriteIcon';
import { Avatar } from '@components/UIKit/Avatar/Avatar';
import { TextEditor } from '@components/UIKit/TextToolbar';
import { PostView } from '@data/post/PostManagerSpec';
import { useGetPublicProfile } from '@hooks/TRPC_Hooks/useGetPublicProfile';
import { getPostCardName } from '@services/posts/helpers';
import { useGetPost } from '@services/posts/hooks/useGetPost';
import { useViewPost } from '@services/posts/hooks/useViewPost';
import { localStorageService } from '@specs/features/storage';
import { getRtoystUrl, LinksEnum } from '@specs/navigation';
import moment from 'moment';
import { FC, useEffect } from 'react';
import s from './postCard.module.scss';
import { PostCardEdit } from './PostCardEdit';
import { PostCardFooter } from './PostCardFooter';
type PostCardProps = {
  initialPost: PostView;
  isPersonal: boolean;
};
const formatCardDate = (date?: Date) => {
  if (!date) return '';
  return moment(date).fromNow();
};
const TIME_BEFORE_POST_VIEWED = 2000;
export const PostCard: FC<PostCardProps> = ({
  initialPost,
  isPersonal
}) => {
  const utils = api.useUtils();
  const {
    data: queryPost
  } = useGetPost({
    post_id: initialPost.id
  });
  const post = queryPost ?? initialPost;

  // Call ALL hooks first, before any conditional returns
  const {
    mutateAsync: viewPost
  } = useViewPost();
  const {
    data: profile
  } = useGetPublicProfile({
    shortId: post?.creator?.shortid ?? ''
  });
  const {
    view
  } = TextEditor({
    content: post?.content_new ?? []
  });
  useEffect(() => {
    if (!post) return;
    utils.PostsManager.getPost.setData({
      post_id: initialPost.id
    }, initialPost);
    const postId = initialPost.id;
    const viewed = initialPost.post_views?.[0]?.id;
    const viewdFromLS = localStorageService.contains('viewedPosts', postId);
    if (!viewdFromLS && !viewed) {
      const timeoutId = window.setTimeout(() => {
        viewPost({
          post_id: postId
        });
      }, TIME_BEFORE_POST_VIEWED);
      return () => clearTimeout(timeoutId);
    }
  }, [initialPost.id, utils, viewPost, post]);

  // Defensive check for post object after all hooks
  if (!post) {
    return null;
  }
  const {
    id,
    publish_date,
    post_reactions,
    type,
    creator
  } = post;
  const link = getRtoystUrl(LinksEnum.POST, id);
  return <div className={s.postCard} data-sentry-component="PostCard" data-sentry-source-file="PostCard.tsx">
            <div className={s.postCard__header}>
                <div className={s.postCard__left}>
                    {type === 'NEWS' ? <SpriteIcon colored width={52} height={52} name={'avatar_platform'} /> : <Avatar className={s.postCard__left__avatar} srcSet={[{
          width: 52,
          height: 52,
          minMedia: 0
        }]} isLoading={false} profile={profile} />}

                    <div className={s.postCard__user}>
                        <div className={s.postCard__name}>{getPostCardName(post)}</div>
                        <span className={s.postCard__date}>{formatCardDate(publish_date)}</span>
                    </div>
                </div>
                <div className={s.postCard__share}>
                    <PostCardEdit postId={post.id} link={link} isPersonal={isPersonal} shortId={creator?.shortid ?? ''} data-sentry-element="PostCardEdit" data-sentry-source-file="PostCard.tsx" />
                </div>
            </div>

            <div className={s.postCard__info}>{view}</div>
            <PostCardFooter link={link} postId={post.id} postReactions={post_reactions} data-sentry-element="PostCardFooter" data-sentry-source-file="PostCard.tsx" />
        </div>;
};